import { memo, useEffect, useState } from "react"
import { IconXmarkOutline18 as X } from "nucleo-ui-outline-18"
import { z } from "zod"

import {
	Dialog,
	Form,
	FormInput,
	IconButton,
	SubmitButton,
	Tooltip,
	useZodForm,
} from "@productlane/ui"
import { mutators } from "@productlane/zero/mutators"

import { ColorPicker } from "@/components/color-picker"
import { toast } from "@/components/toast"
import { generateId } from "@/lib/id"
import { getProjectIcon } from "@/lib/projects"
import { mutateOk, useZero } from "@/lib/zero"

const DEFAULT_COLOR = "#6B7280"

const CreateChangelogTagFormSchema = z.object({
	name: z.string().min(1),
	icon: z.string().nullable(),
})

interface CreateChangelogTagDialogProps {
	isOpen: boolean
	initialName?: string
	onOpenChange: (
		value: boolean,
		created?: { id: string; name: string; color: string; icon: string | null },
	) => void
}

export const CreateChangelogTagDialog = memo(
	({ isOpen, initialName, onOpenChange }: CreateChangelogTagDialogProps) => {
		const zero = useZero()
		const [color, setColor] = useState(DEFAULT_COLOR)

		const form = useZodForm({
			schema: CreateChangelogTagFormSchema,
			defaultValues: {
				name: "",
				icon: null,
			},
		})

		const iconWatch = form.watch("icon")
		const nameWatch = form.watch("name")

		useEffect(() => {
			if (!isOpen) return
			const trimmedInitial = initialName?.trim()
			if (!trimmedInitial) return
			if (nameWatch.trim().length > 0) return
			form.setValue("name", trimmedInitial)
		}, [isOpen, initialName, nameWatch, form])

		return (
			<Dialog
				isOpen={isOpen}
				onOpenChange={(open) => {
					if (!open) {
						form.reset()
						setColor(DEFAULT_COLOR)
					}
					onOpenChange(open)
				}}
				className="m-2 max-w-md p-0"
				title={<div className="p-6 pb-0">Create changelog label</div>}
			>
				<Form
					id="create-changelog-tag-form"
					form={form}
					className="flex flex-col"
					onSubmit={async (values) => {
						const id = generateId()
						await mutateOk(
							zero.mutate(
								mutators.changelogTags.create({
									id,
									color,
									icon: values.icon,
									name: values.name,
								}),
							),
						)
						onOpenChange(false, {
							id,
							name: values.name,
							color,
							icon: values.icon,
						})
						toast.success({ title: "Changelog label created" })
						form.reset()
						setColor(DEFAULT_COLOR)
					}}
				>
					<div className="flex items-center gap-2 px-6 pb-6 pt-2">
						<ColorPicker
							value={color}
							onChange={setColor}
							withIcon
							icon={iconWatch}
							onIconChange={(icon) => form.setValue("icon", icon)}
							withInputs
						>
							<button
								type="button"
								className="flex aspect-square h-8 w-8 items-center justify-center rounded-lg border"
							>
								{iconWatch ? (
									(() => {
										const Icon = getProjectIcon(iconWatch)
										return <Icon className="h-4 w-4" style={{ color }} />
									})()
								) : (
									<div
										className="h-3 w-3 rounded-full"
										style={{ backgroundColor: color }}
									/>
								)}
							</button>
						</ColorPicker>

						<FormInput
							textSize="xl"
							variant="borderless"
							name="name"
							placeholder="Label name"
						/>
						{iconWatch && (
							<Tooltip label="Remove icon">
								<IconButton
									className="text-xs text-tertiary hover:text-secondary"
									onClick={() => form.setValue("icon", null)}
									Icon={X}
									variant="ghost"
								/>
							</Tooltip>
						)}
					</div>

					<div className="flex items-center justify-end p-6">
						<SubmitButton form="create-changelog-tag-form" disabled={false}>
							Create label
						</SubmitButton>
					</div>
				</Form>
			</Dialog>
		)
	},
)
